/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from 'react'

import { FormContext, useForm } from 'react-hook-form'
import PropTypes from 'prop-types'
import { Button, Box, Typography } from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import { useHistory } from 'react-router-dom'

import {
  PermissionsForm,
  GeneralInformationForm,
  AdminsForm,
  CompanySegmentationForm,
  SalesForm,
  AdvisorPermissionsForm,
} from './components'
import { LoadingBox } from 'components'

import useSnackbar from 'hooks/useSnackbar'

import constants from 'constants/index'
import helpers from 'helpers'
import * as service from 'service'
import { routes } from 'Routes'

import schema from './schema'

const ProfilesForm = ({ profileType, isEdit, profile }) => {
  const [loading, setLoading] = useState(true)
  const [permissions, setPermissions] = useState({})

  const history = useHistory()
  const snackbar = useSnackbar()

  const profileStatus = profile?.status

  const formMethods = useForm({
    validationSchema: schema(profileType, isEdit),
    defaultValues: {
      name: profile?.name || '',
      description: profile?.description || '',
      status: profile?.status || false,
      default: profile?.default || null,
      companyId: profile?.company || null,
      organizationId: profile?.organization || null,
      profileType: profileType,
      originProfileId: profile?.originProfileId || null,
    },
  })

  const { handleSubmit, reset } = formMethods

  const isCollaborator =
    profileType === constants.user.PROFILE_TYPE_COLLABORATOR
  const isAdvisor = profileType === constants.user.PROFILE_TYPE_ADVISOR
  const isClient = profileType === constants.user.PROFILE_TYPE_CLIENT

  const redirectRollBack = () => {
    if (isCollaborator) return history.push(routes.collaboratorProfiles.all)
    if (isClient) return history.push(routes.userProfiles.all)

    return history.push(routes.userProfiles.organizations)
  }

  const onSubmit = (data) => {
    isEdit ? handleEdit(data) : handleCreate(data)
  }

  const handleEdit = async (data) => {
    try {
      setLoading(true)
      if (isCollaborator) {
        await service.dponet.collaboratorProfiles.edit({
          profileId: profile.id,
          profile: { ...data, permissions },
        })
      } else {
        if (isAdvisor) {
          await service.backoffice.advisorProfiles.edit({
            profileId: profile.id,
            profile: { ...data, permissions },
          })
        } else {
          await service.backoffice.userProfiles.edit({
            profileId: profile.id,
            profile: { ...data, permissions },
          })
        }
      }

      snackbar.open({
        message: 'Perfil editado com sucesso',
        variant: 'success',
      })
      redirectRollBack()
    } catch (error) {
      snackbar.open({
        message: helpers.formatters.errorMessage(error?.response?.data?.error),
        variant: 'error',
      })
      reset(data)
      setLoading(false)
    }
  }

  const handleCreate = async (data) => {
    try {
      setLoading(true)
      if (isCollaborator) {
        await service.dponet.collaboratorProfiles.create({
          profile: { ...data, permissions },
        })
      } else {
        if (isAdvisor) {
          await service.backoffice.advisorProfiles.create({
            profile: { ...data, permissions },
          })
        } else {
          await service.backoffice.userProfiles.create({
            profile: { ...data, permissions },
          })
        }
      }

      snackbar.open({
        message: 'Perfil criado com sucesso',
        variant: 'success',
      })
      redirectRollBack()
    } catch (error) {
      snackbar.open({
        message: helpers.formatters.errorMessage(error?.response?.data?.error),
        variant: 'error',
      })
      reset(data)
      setLoading(false)
    }
  }

  const handleChange = (value, tag) => {
    setPermissions({ ...permissions, [tag]: value })
  }

  useEffect(() => {
    setLoading(true)
    if (profile) {
      for (let item of profile.profileFunctionalities) {
        permissions[item.tag] = item.status
      }
      setPermissions(permissions)
    }
    setLoading(false)
  }, [profile])

  return (
    <>
      {loading ? (
        <LoadingBox />
      ) : (
        <>
          <FormContext {...formMethods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              {profile?.default && (
                <Box mb={2}>
                  <Alert variant="filled" severity="info">
                    <Typography>
                      Atenção! Este é um perfil sugerido do sistema, isto
                      significa que uma cópia do mesmo existe em cada empresa de
                      nossa plataforma, realizar alterações neste perfil não
                      impactará em atualizações nos perfis já copiados a partir
                      deste, as atualizações serão aplicadas somente para novas
                      cópias deste perfil, ou seja, na criação de uma nova
                      empresa em nossa plataforma.
                    </Typography>
                  </Alert>
                </Box>
              )}
              <GeneralInformationForm
                profileType={profileType}
                profile={profile}
                isEdit={isEdit}
              />
              {isClient ? (
                <PermissionsForm
                  permissions={permissions}
                  handleChange={handleChange}
                  activeProfile={profileStatus}
                  isEdit={isEdit}
                />
              ) : isCollaborator ? (
                <>
                  <AdminsForm
                    permissions={permissions}
                    handleChange={handleChange}
                    activeProfile={profileStatus}
                    isEdit={isEdit}
                  />
                  <SalesForm
                    permissions={permissions}
                    handleChange={handleChange}
                  />
                  <CompanySegmentationForm
                    permissions={permissions}
                    handleChange={handleChange}
                    activeProfile={profileStatus}
                    isEdit={isEdit}
                  />
                </>
              ) : (
                <AdvisorPermissionsForm
                  permissions={permissions}
                  handleChange={handleChange}
                  activeProfile={profileStatus}
                  isEdit={isEdit}
                />
              )}
              <Box mt={2} display="flex" justifyContent="flex-start">
                <Box pr={1}>
                  <Button
                    type="button"
                    variant="outlined"
                    onClick={redirectRollBack}
                  >
                    Voltar
                  </Button>
                </Box>
                {(!isEdit || profileStatus) && (
                  <Button color="primary" variant="contained" type="submit">
                    Salvar
                  </Button>
                )}
              </Box>
            </form>
          </FormContext>
        </>
      )}
    </>
  )
}

ProfilesForm.propTypes = {
  clientProfile: PropTypes.bool,
  isEdit: PropTypes.bool,
  profile: PropTypes.object,
}

export default ProfilesForm
